import { usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { ButtonDock } from "@ui/components/ButtonDock";
import { Dialog } from "@ui/components/Dialog";
import { Illustration } from "@ui/components/Illustration";
import { StandardButton } from "@ui/components/StandardButton";
import { messageThreadHref } from "@/lib/routes";
import type { SharedProps } from "@/types";

// Shape shared by the paid confirmation serializer and the free flow's
// `success` object (both built by Marketplace::Values::PurchaseConfirmation).
export interface PurchaseSuccess {
  sqid: string;
  masked_email: string;
  download_url: string | null;
  delivery: "file" | "link" | null;
  seller_handle: string | null;
}

interface PurchaseSuccessModalProps extends PurchaseSuccess {
  onClose: () => void;
}

/**
 * Post-purchase success modal, shared by the paid confirmation (Stripe redirect)
 * and the free in-place flow. The header confirms the purchase; the body + CTA
 * adapt to what the buyer wants next: download a file, open linked content,
 * message the seller (services), or, for guests who can't chat, the emailed link.
 * Dismiss is the header X — the footer only carries a real next step.
 */
export function PurchaseSuccessModal({
  masked_email,
  download_url,
  delivery,
  seller_handle,
  onClose,
}: PurchaseSuccessModalProps) {
  const { current_user } = usePage<SharedProps>().props;

  let description: string;
  let ctaLabel: string;
  // The CTA either navigates somewhere (download / open / message) or just
  // dismisses the modal (email-led cases, where the next step is in the inbox).
  let ctaTarget: { href: string; newTab?: boolean } | { onPress: () => void };

  if (download_url && delivery === "link") {
    description = t("web.purchases.confirmations.show.body_open", { email: masked_email });
    ctaLabel = t("web.purchases.confirmations.show.cta_open");
    ctaTarget = { href: download_url, newTab: true };
  } else if (download_url) {
    description = t("web.purchases.confirmations.show.body_download", { email: masked_email });
    ctaLabel = t("web.purchases.confirmations.show.cta_download");
    ctaTarget = { href: download_url, newTab: true };
  } else if (seller_handle && current_user) {
    description = t("web.purchases.confirmations.show.body_service", { email: masked_email });
    ctaLabel = t("web.purchases.confirmations.show.cta_message");
    ctaTarget = { href: messageThreadHref(seller_handle) };
  } else {
    // Guest service / non-deliverable: the email is the path forward, so the
    // only modal action is to dismiss.
    description = seller_handle
      ? t("web.purchases.confirmations.show.body_service_guest", { email: masked_email })
      : t("web.purchases.confirmations.show.body_generic", { email: masked_email });
    ctaLabel = t("web.purchases.confirmations.show.cta_done");
    ctaTarget = { onPress: onClose };
  }

  const primaryAction =
    "href" in ctaTarget ? (
      <StandardButton
        color="pink"
        href={ctaTarget.href}
        rel={ctaTarget.newTab ? "noopener noreferrer" : undefined}
        size="44"
        target={ctaTarget.newTab ? "_blank" : undefined}
      >
        {ctaLabel}
      </StandardButton>
    ) : (
      <StandardButton color="pink" onPress={ctaTarget.onPress} size="44">
        {ctaLabel}
      </StandardButton>
    );

  return (
    <>
      <Dialog.Header onClose={onClose} title={t("web.purchases.confirmations.show.heading")} />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Illustration className="mx-auto w-40" name="celebrations" />
          <p className="text-base text-neutral-700">{description}</p>
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        <ButtonDock primaryAction={primaryAction} />
      </Dialog.Footer>
    </>
  );
}
